import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Grid3x3, LogOut } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SessionBadge } from './SessionBadge';
import { ThemeSwitcher } from './ThemeSwitcher';
import { useThemeStore } from '@/lib/theme';
import { themes } from '@/themes/manifest';
import { apps } from '@/apps/registry';

export function PortalHeader() {
  const { pathname } = useLocation();
  const activeApp = apps.find(
    (app) => !app.externalUrl && (pathname === app.to || pathname.startsWith(`${app.to}/`)),
  );
  const id = useThemeStore((s) => s.theme);
  const mode = themes.find((t) => t.id === id)?.mode ?? 'light';
  // Same brand-logo logic as HMHWatch: white logo on dark themes, colored on light.
  const prefersDark =
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  const useWhite = mode === 'dark' || (mode === 'auto' && prefersDark);
  const logoSrc = useWhite ? '/img/hmhLogoDark.png' : '/img/hmhLogo.png';

  return (
    <header className="border-b border-border bg-surface-elevated">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 min-w-0" aria-label="HMH Apps — launcher">
          <img
            src={logoSrc}
            srcSet={`${logoSrc} 2x`}
            alt=""
            className="h-6 md:h-7 w-auto block flex-shrink-0"
            aria-hidden
          />
          <span className="font-(family-name:--font-heading) text-lg md:text-xl font-bold text-text truncate">
            HMH Apps
            {activeApp && (
              <span className="font-normal text-muted"> · {activeApp.name}</span>
            )}
          </span>
        </Link>
        <div className="flex items-center gap-1 flex-shrink-0">
          <AppSwitcher />
          <ThemeSwitcher />
          {/* Account dropdown (username + Sign out) — existed since the HMHWatch clone but
              was never mounted; surfaced 2026-07-08 on owner ask. */}
          <SessionBadge />
        </div>
      </div>
    </header>
  );
}

function AppSwitcher() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const logout = useMutation({
    mutationFn: () => api.post('/api/logout'),
    onSettled: () => {
      qc.clear();
      nav('/login', { replace: true });
    },
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Switch app" title="Switch app">
          <Grid3x3 className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[16rem]">
        <DropdownMenuLabel>Switch app</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/">All apps (launcher)</Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {apps.map((app) => {
          const Icon = app.Icon;
          const content = (
            <>
              <Icon className="h-4 w-4 text-accent flex-shrink-0" aria-hidden />
              <span className="flex-1 min-w-0 truncate">{app.name}</span>
            </>
          );
          return (
            <DropdownMenuItem key={app.id} asChild>
              {app.externalUrl ? (
                <a href={app.externalUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2">
                  {content}
                </a>
              ) : (
                <Link to={app.to} className="flex items-center gap-2">
                  {content}
                </Link>
              )}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => logout.mutate()} className="text-accent-danger">
          <LogOut className="h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
